import {
	createApi,
	fetchBaseQuery,
	type BaseQueryApi,
	type BaseQueryFn,
	type FetchArgs,
	type QueryReturnValue,
} from "@reduxjs/toolkit/query/react"
import { API_CODE, BACKEND_URL, RATE_LIMIT_BYPASS_KEY } from "@/const"
import { locData } from "@utils/localization"
import i18n from "@/i18n"
import { logoutUser, createRefreshCapableBaseQuery, type AuthState } from "@myeway/auth"
import toast from "react-hot-toast"
import type { LanguageCode } from "@myeway/types"
import { setRateLimited } from "@redux/rateLimit"

/** Prepares the headers for base query's fetch function. */
export const prepareHeaders = (headers: Headers, { getState }: Pick<BaseQueryApi, "getState">) => {
	const { childId } = (getState() as { user: { childId: string } }).user
	if (childId) headers.set("x-child-id", childId)

	const lang = i18n.language || "fi"
	headers.set("Accept-Language", lang)

	if (RATE_LIMIT_BYPASS_KEY) {
		headers.set("x-bypass-rate-limit", RATE_LIMIT_BYPASS_KEY)
	}

	return headers
}

const baseQueryWithAuth = fetchBaseQuery({
	baseUrl: `${BACKEND_URL}/api`,
	prepareHeaders,
	fetchFn: async (input, init) => fetch(input, { ...init, credentials: "include" }),
	responseHandler: "content-type",
})

const refreshCapableBaseQuery = createRefreshCapableBaseQuery(baseQueryWithAuth)

export type ErrorType = {
	status: number
	data: {
		message: string
		code: string
	}
}

function isRefreshError(error: ErrorType, request: Request) {
	const pathname = new URL(request.url).pathname
	const isRefreshPath = pathname.startsWith("/refresh") || pathname.startsWith("/back-end/refresh")
	const authenticationRelatedErrorCodes = [
		API_CODE.ACCESS_TOKEN_MISSING,
		API_CODE.ACCESS_TOKEN_INVALID,
		API_CODE.ACCESS_TOKEN_EXPIRED,
	]
	return (isRefreshPath && error.status == 500) || authenticationRelatedErrorCodes.includes(error.data?.code)
}

const customBaseQuery: BaseQueryFn<string | FetchArgs, unknown, ErrorType> = async (args, baseQueryApi, extraOptions) => {
	const authState = (baseQueryApi.getState() as { auth: AuthState }).auth
	const rateLimitState = (baseQueryApi.getState() as { rateLimit: { rateLimitedUntil: number | null } }).rateLimit

	let usesRefreshCapableQuery = true

	// Depending on auth state, we want to execute specific base query.
	// Unauthenticated endpoints cannot be fetched via `refreshCapableBaseQuery`, because it auto-refreshes.
	let result: QueryReturnValue<unknown | FetchArgs, ErrorType, object | undefined>
	if (!authState.status || authState.status === "idle" || authState.status === "unauthenticated") {
		usesRefreshCapableQuery = false
		result = (await baseQueryWithAuth(args, baseQueryApi, extraOptions)) as QueryReturnValue<
			unknown,
			ErrorType,
			object | undefined
		>
	} else {
		result = (await refreshCapableBaseQuery(args, baseQueryApi, extraOptions)) as QueryReturnValue<
			unknown,
			ErrorType,
			object | undefined
		>
	}

	const request = (result.meta as { request: Request }).request
	const response = (result.meta as { response: Response }).response
	const { error } = result

	if (usesRefreshCapableQuery && error && isRefreshError(error, request)) {
		toast.error("Kirjaudu sisään uudelleen", {
			id: "login-again",
		})
		baseQueryApi.dispatch(logoutUser({}))

		// Reset state.
		// Might send a few additional requests (due to RTK's query queue) without doing this
		baseQueryApi.dispatch(api.util.resetApiState())

		// Skip any further query logic by returning undefined
		return { data: "Logged out" }
	}

	const rateLimitRelatedErrorCodes = [API_CODE.TOO_MANY_REQUESTS]
	if (
		error &&
		(rateLimitState.rateLimitedUntil === null || rateLimitState.rateLimitedUntil < Date.now()) &&
		rateLimitRelatedErrorCodes.includes(error.data?.code)
	) {
		const rateLimitedUntil = +response.headers.get("x-rate-limited-until")!
		baseQueryApi.dispatch(setRateLimited(rateLimitedUntil))
	}

	// Localize responses by default unless opted out
	if (!(typeof args !== "string" && args.params?.noLoc)) {
		result = locData<typeof result>(result, i18n.language as LanguageCode)
	}

	return result
}

export type BaseQueryType = typeof customBaseQuery

export const api = createApi({
	reducerPath: "api",
	tagTypes: [
		"Loc", // Localization
		"User",
		"Profile",
		"Training",
		"Location",
		"TrainingResult",
		"EventList",
		"Event",
		"TestProtocol",
		"Participant",
		"Group",
		"RootGroup",
		"TemplateRole",
		"GlobalSpecial",
		"Preferences",
		"RootGroupClass",
		"Diary",
		"Survey",
		"TaskDefinition",
		"HelpFile",
		"ConsentTermsFile",
		"PrivacyPolicyFile",
		"CustomerGroup",
		"Notification",
		"Video",
		"PeriodPlanner",
		"PeriodPlannerTemplate",
		"EventType",
		"Debug",
		"Analytics",
		"EventTypeClass",
		"EventTemplate",
		"Migration",
		"PushNotificationEnabledStatuses",
		"ClubSupportPlayers",
		"RecommendationList",
		"PlayerGoal",
		"PlayerDocument",
		"Exercise",
		"ExerciseSet",
	],
	baseQuery: customBaseQuery,
	endpoints: () => ({}),
})
